// components/overlay/FlashDesignsPanel.jsx
// Shared "Today's Flash Pancakes" board for OBS overlays.
// No per-tile frames, no subtitles — just image + name. Layout is sized so all
// designs fit within the panel without scrolling.
// Used by Booth (menu board), Break (BRB screen), and StartingSoon so the
// flash menu stays visible at all times during service.
//
// Pass `allDesigns` from useFlashDesigns (NOT `designs`) so the freestyle "?"
// tile shows on every menu surface — Dan wants the mystery option visible
// wherever the flash board appears (2026-07-04).
//
// Layouts:
//   grid (default)  — CSS grid, cols from the `cols` prop / orientation.
//   stagger         — two offset rows (ceil/floor split), the bottom row
//                     centered so it nests half-a-tile off the top row and
//                     tucks up into its gaps (slight negative margin). Packs
//                     8+1 designs without shrinking anyone. Used by the Booth
//                     portrait band + Break's bottom band.
//
// spotlight (Booth gala mode): small curated sets (~3 designs) in a single
// evenly-spaced column instead of the grid, each on a dark radial backdrop —
// pink-heavy art disappears into themed (zoofari) column gradients without it.
// Freestyle is excluded there (curated pitch surface, not a menu). Static
// styles only; the drop-shadow is rasterized once, never animated.

const GOLD = "var(--jop-gold)";
const CREAM = "var(--jop-cream)";
const D = "var(--jop-display)";

// One design tile — image (or "?" fallback) over its name.
function FlashTile({ design, labelFs = "1.05rem" }) {
  return (
    <div style={{
      minWidth: 0, minHeight: 0,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: "0.2rem",
      overflow: "hidden",
    }}>
      {design.imageUrl ? (
        <img
          src={design.imageUrl}
          alt={design.label}
          style={{
            flex: 1, minHeight: 0,
            maxWidth: "100%", maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <div style={{
          flex: 1, minHeight: 0, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: GOLD, fontFamily: D, fontWeight: 900,
          fontSize: "3rem",
          textShadow: "0 2px 10px rgba(0,0,0,0.4)",
        }}>?</div>
      )}
      <div style={{
        color: CREAM, fontFamily: D, fontWeight: 700,
        fontSize: labelFs, lineHeight: 1.1, textAlign: "center",
        flexShrink: 0,
        maxWidth: "100%",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {design.label}
      </div>
    </div>
  );
}

export default function FlashDesignsPanel({
  designs,
  portrait,
  spotlight = false,
  cols: colsProp,
  labelMode = "header", // "header" | "none"
  stagger = false,
}) {
  if (!designs || designs.length === 0) return null;

  // Spotlight's single column only fits small curated sets — with a full-size
  // set the fixed-height labels eat the column and the flex:1 image boxes
  // collapse to ~0 (names render, art invisible). Fall back to the grid.
  // Freestyle never shows here: it's a curated pitch surface, not a menu.
  const spotDesigns = designs.filter((d) => !d.isFreestyle);
  if (spotlight && spotDesigns.length <= 4) {
    return (
      <div style={{
        flex: 1, minHeight: 0, overflow: "hidden",
        display: "flex", flexDirection: "column", gap: "0.4rem",
      }}>
        <div style={{
          color: GOLD, fontFamily: D, fontWeight: 700,
          fontSize: "1.3rem", textTransform: "uppercase",
          letterSpacing: "0.08em",
          textAlign: "center",
          padding: "0.2rem 0 0.4rem",
          borderBottom: "2px solid rgba(var(--jop-gold-rgb), 0.33)",
          flexShrink: 0,
        }}>
          🥞 Today's Flash Pancakes
        </div>
        <div style={{
          flex: 1, minHeight: 0, overflow: "hidden",
          display: "flex",
          flexDirection: portrait ? "row" : "column",
          justifyContent: "space-evenly",
          alignItems: "stretch",
        }}>
          {spotDesigns.map((d) => (
            <div key={d.id} style={{
              flex: 1, minWidth: 0, minHeight: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              overflow: "hidden",
              padding: "0.35rem 0",
            }}>
              <div style={{
                flex: 1, minHeight: 0, width: "100%",
                display: "flex", alignItems: "center", justifyContent: "center",
                // Theme navy, not ink — black read as a hole in the flamingo
                // gradient (Wella). Zoofari maps navy to deep plum #5c2742.
                background: "radial-gradient(ellipse closest-side, rgba(var(--jop-navy-rgb), 0.65) 0%, rgba(var(--jop-navy-rgb), 0.38) 55%, transparent 100%)",
              }}>
                <img
                  src={d.imageUrl}
                  alt={d.label}
                  style={{
                    maxWidth: "86%", maxHeight: "94%",
                    objectFit: "contain",
                    filter: "drop-shadow(0 5px 12px rgba(var(--jop-navy-rgb), 0.6))",
                  }}
                />
              </div>
              <div style={{
                color: CREAM, fontFamily: D, fontWeight: 700,
                fontSize: "1.2rem", lineHeight: 1.1, textAlign: "center",
                flexShrink: 0,
                marginTop: "2px",
                maxWidth: "100%",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {d.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── STAGGERED PACK — two offset rows, same tile size, tighter fit ─────────
  if (stagger) {
    const topCount = Math.ceil(designs.length / 2);
    const top = designs.slice(0, topCount);
    const bottom = designs.slice(topCount);
    // Every tile in BOTH rows gets the same fixed width slice (derived from
    // the wider row) — the shorter, centered bottom row then lands half a
    // tile off the top row's columns, and the slight pull-up nests it into
    // the top row's gaps. Nobody shrinks; the pack just tightens.
    const tileW = `${100 / topCount}%`;
    const row = (items, nest) => (
      <div key={nest ? "b" : "t"} style={{
        flex: 1, minHeight: 0,
        display: "flex", justifyContent: "center", alignItems: "stretch",
        gap: "0.3rem",
        marginTop: nest ? "-0.9rem" : 0,
      }}>
        {items.map((d) => (
          <div key={d.id} style={{ flex: `0 0 calc(${tileW} - 0.3rem)`, minWidth: 0, display: "flex" }}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
              {/* Stagger only renders in the portrait bands (1080×1920 on a
                  phone stream / booth panel at distance) — names need menu-
                  board scale, not caption scale. */}
              <FlashTile design={d} labelFs="1.5rem" />
            </div>
          </div>
        ))}
      </div>
    );
    return (
      <div style={{
        flex: 1, minHeight: 0, overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {row(top, false)}
        {bottom.length > 0 && row(bottom, true)}
      </div>
    );
  }

  // ── GRID ───────────────────────────────────────────────────────────────────
  // cols is overridable per-scene; rows always derive from the design count,
  // so however many designs are active they scale to fit the panel — the
  // panel never grows.
  const cols = colsProp || (portrait ? 3 : 2);
  const rows = Math.max(1, Math.ceil(designs.length / cols));

  const grid = (
    <div style={{
      flex: 1, minHeight: 0, overflow: "hidden",
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      gap: cols >= 4 ? "0.3rem 0.45rem" : "0.4rem 0.6rem",
    }}>
      {designs.map((d) => (
        <FlashTile key={d.id} design={d} />
      ))}
    </div>
  );

  if (labelMode === "none") {
    return (
      <div style={{
        flex: 1, minHeight: 0, overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {grid}
      </div>
    );
  }

  return (
    <div style={{
      flex: 1, minHeight: 0, overflow: "hidden",
      display: "flex", flexDirection: "column", gap: "0.6rem",
    }}>
      <div style={{
        color: GOLD, fontFamily: D, fontWeight: 700,
        fontSize: "1.3rem", textTransform: "uppercase",
        letterSpacing: "0.08em",
        textAlign: "center",
        padding: "0.2rem 0 0.4rem",
        borderBottom: "2px solid rgba(var(--jop-gold-rgb), 0.33)",
        flexShrink: 0,
      }}>
        🥞 Today's Flash Pancakes
      </div>
      {grid}
    </div>
  );
}
